import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import IndustriesData from '../../../data/industriesData';

const SEGMENT_VH = 70;
const STACK_OFFSET = 30;

const IndustriesShowcase = () => {
   const [activeIndex, setActiveIndex] = useState(0);
   const [isPinned, setIsPinned] = useState(false);
   const [cardHeight, setCardHeight] = useState(0);
   const pinRef = useRef(null);
   const spacerRef = useRef(null);
   const activeCardRef = useRef(null);

   useEffect(() => {
      const mm = gsap.matchMedia();
      mm.add('(min-width: 768px)', () => {
         const st = ScrollTrigger.create({
            trigger: spacerRef.current,
            start: 'top top',
            end: 'bottom bottom',
            pin: pinRef.current,
            onToggle: (self) => setIsPinned(self.isActive),
            onUpdate: (self) => {
               const idx = Math.min(
                  IndustriesData.length - 1,
                  Math.floor(self.progress * IndustriesData.length)
               );
               setActiveIndex((prev) => (prev !== idx ? idx : prev));
            },
         });
         return () => st.kill();
      });
      return () => {
         mm.revert();
         setIsPinned(false);
      };
   }, []);

   useLayoutEffect(() => {
      if (!activeCardRef.current) return;
      const measure = () => setCardHeight(activeCardRef.current.offsetHeight);
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(activeCardRef.current);
      return () => ro.disconnect();
   }, [activeIndex]);

   const handleTabClick = (index) => {
      setActiveIndex(index);
      const spacer = spacerRef.current;
      if (spacer && window.innerWidth >= 768) {
         const scrollableRange = spacer.offsetHeight - window.innerHeight;
         const targetY = spacer.offsetTop + ((index + 0.5) / IndustriesData.length) * scrollableRange;
         window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
   };

   const visibleIndustries = IndustriesData.slice(0, activeIndex + 1);
   const stackHeight = cardHeight + activeIndex * STACK_OFFSET;

   return (
      <section className="industries-showcase pt-10 pb-60">
         <div className="tw-container tw-mx-auto">
            <div className="section__title section__title-3 tw-text-center mb-45">
               <span>Who We Serve</span>
               <h2>Industries We Served</h2>
            </div>

            <div ref={spacerRef} className="industries-showcase__spacer" style={{ '--segment-count': IndustriesData.length, '--segment-vh': `${SEGMENT_VH}vh` }}>
               <div ref={pinRef} className={`industries-showcase__pin${isPinned ? ' is-pinned' : ''}`}>
                  <div className="industries-showcase__tabs" role="tablist" aria-label="Industries">
                     {IndustriesData.map((item, index) => (
                        <button
                           key={item.id}
                           type="button"
                           role="tab"
                           aria-selected={index === activeIndex}
                           className={`industries-showcase__tab${index === activeIndex ? ' is-active' : ''}`}
                           onClick={() => handleTabClick(index)}
                        >
                           {item.name}
                        </button>
                     ))}
                  </div>

                  <div className="industries-showcase__panel" style={stackHeight ? { height: stackHeight } : undefined}>
                     {visibleIndustries.map((industry, index) => {
                        const isActive = index === activeIndex;
                        return (
                           <div
                              key={industry.id}
                              ref={isActive ? activeCardRef : undefined}
                              className={`industries-showcase__panel-content${isActive ? ' is-active' : ''}`}
                              style={{ backgroundColor: industry.bgColor, top: index * STACK_OFFSET, zIndex: index + 1 }}
                           >
                              <div className="industries-showcase__panel-text">
                                 <span className="industries-showcase__eyebrow">Industries We Served</span>
                                 <h3 className="industries-showcase__heading">{industry.name}</h3>
                                 <p className="industries-showcase__desc">{industry.tagline}</p>
                                 <Link to={`/industries/${industry.slug}`} className="industries-showcase__cta">Get Started</Link>
                              </div>
                              <div className="industries-showcase__panel-graphic" aria-hidden="true">
                                 <span className="industries-showcase__panel-icon">{industry.icon}</span>
                              </div>
                           </div>
                        );
                     })}
                  </div>
               </div>
            </div>
         </div>
      </section>
   );
};

export default IndustriesShowcase;
